/**
 * useLaunchFilter Hook
 *
 * Encapsulates the logic for:
 * - Fetching launches with their associated codes
 * - Managing selected launch state
 * - Providing codes for filtering submissions
 *
 * Follows Single Responsibility Principle - only handles launch filtering logic
 */

import { useMemo } from "react";
import { useAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import { selectedLaunchIdAtom } from "../store";
import type { LaunchWithCodes } from "../store/types";

interface UseLaunchFilterResult {
  /** List of launches with their codes and submission counts */
  launches: LaunchWithCodes[] | undefined;
  /** Whether launches are being fetched */
  isLoading: boolean;
  /** Currently selected launch ID */
  selectedLaunchId: string;
  /** Set the selected launch ID */
  setSelectedLaunchId: (id: string) => void;
  /** Codes for the currently selected launch (null if no launch selected) */
  selectedLaunchCodes: string[] | null;
  /** Clear launch selection */
  clearLaunchSelection: () => void;
}

/**
 * Hook for managing launch-based filtering of submissions
 *
 * @example
 * ```tsx
 * const { launches, selectedLaunchCodes, setSelectedLaunchId } = useLaunchFilter();
 *
 * // Filter submissions by selected launch codes
 * const filtered = submissions.filter(s => {
 *   if (!selectedLaunchCodes) return true;
 *   return selectedLaunchCodes.includes(s.code);
 * });
 * ```
 */
export function useLaunchFilter(): UseLaunchFilterResult {
  const { projectId } = useProject();
  const [selectedLaunchId, setSelectedLaunchId] = useAtom(selectedLaunchIdAtom);

  // Query launches with their form mappings and codes
  const { data: launches, isLoading } = useQuery({
    queryKey: ["launches-with-submissions", projectId],
    queryFn: async (): Promise<LaunchWithCodes[]> => {
      // Get launches with their form mappings and codes
      const { data: launchesData, error } = await supabase
        .from("form_launches")
        .select(`
          id,
          jotform_form_mappings:form_mapping_id (
            id,
            jotform_form_id,
            form_title
          ),
          clientes (
            id,
            nombre
          ),
          form_codes (
            code
          )
        `)
        .eq("project_id", projectId)
        .not("form_mapping_id", "is", null);

      if (error) throw error;

      // Filter to only launches that have submissions
      const launchesFiltered: LaunchWithCodes[] = [];

      for (const launch of launchesData || []) {
        if (!launch.jotform_form_mappings) continue;

        const formMapping = launch.jotform_form_mappings as {
          jotform_form_id: string;
          form_title: string | null;
        };
        const codes =
          (launch.form_codes as { code: string }[] | null)?.map(
            (c) => c.code
          ) || [];

        // Check if this form has submissions
        const { count } = await supabase
          .from("jotform_submissions")
          .select("*", { count: "exact", head: true })
          .eq("jotform_form_id", formMapping.jotform_form_id)
          .eq("project_id", projectId);

        if (count && count > 0) {
          launchesFiltered.push({
            id: launch.id,
            jotform_form_id: formMapping.jotform_form_id,
            form_title: formMapping.form_title,
            client_name:
              (launch.clientes as { nombre: string } | null)?.nombre ||
              "Sin cliente",
            submission_count: count,
            codes,
          });
        }
      }

      return launchesFiltered;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Derive selected launch codes from selected launch
  const selectedLaunchCodes = useMemo(() => {
    if (!selectedLaunchId || !launches) return null;
    const launch = launches.find((l) => l.id === parseInt(selectedLaunchId));
    return launch?.codes || null;
  }, [selectedLaunchId, launches]);

  const clearLaunchSelection = () => {
    setSelectedLaunchId("");
  };

  return {
    launches,
    isLoading,
    selectedLaunchId,
    setSelectedLaunchId,
    selectedLaunchCodes,
    clearLaunchSelection,
  };
}
